import {Injectable} from '@angular/core'
import {
  throwError as observableThrowError,
  defer,
  EMPTY,
  Observable,
  of,
  Subject,
  Subscription,
  timer,
} from 'rxjs'
import {
  catchError,
  defaultIfEmpty,
  flatMap,
  map,
  switchMap,
  tap,
  filter,
  first,
  retry,
  share,
  shareReplay,
  finalize,
} from 'rxjs/operators'
import {AuthToken, AuthService, SsoUser} from '@app/core/services/rest-api'
import {UserFeaturesService} from './user-features.service'
import {ContextService} from '@app/core/services/context.service'
import {LocalStorageService} from '@app/core/services/local-storage.service'
import {
  AuthHelperService,
  TokenType,
} from '@app/core/services/auth-helper.service'
import {
  decodeJwt,
  isJwtExpired,
  millisUntilExpiry,
} from '@app/core/util/jwt.util'
import {rememberSsoLoginError} from '@app/core/util/sso-login-error.util'
import {
  SsoAuthService,
  SsoTransientRefreshError,
} from '@app/core/services/sso-auth.service'
import {AccessControlService} from '@app/core/services/access-control/access-control.service'
import {NotificationService, NotificationType} from './notification.service'
import {LogoutCoordinatorService} from './logout-coordinator.service'

const MEMORY_KEY = 'credential-memory'
const TOKEN_KEY = 'auth-token'
const REFRESH_TOKEN_KEY = 'refresh-token'
const TOKEN_TYPE_KEY = 'auth-token-type'
const ID_TOKEN_KEY = 'sso-id-token'

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  tokenExpirationNotificationListener$: Observable<any> // should show modal
  tokenExpirationListener$: Observable<any> // should redirect to login from non-public routes
  resetTokenExpirationTimer$ = new Subject<{
    notification: number
    redirect: number
  }>()
  refreshTokenNotificationBuffer = 60 * 1000 // 1 minute
  /**
   * Minimum lead before a bearer token's `exp` to silently renew it. The
   * actual lead is the larger of this and 40% of the token's lifetime, so a
   * throttled background-tab timer (Chrome fires at most once a minute when
   * hidden) or a small clock skew still renews before the backend rejects it.
   */
  private bearerRefreshMinLeadMs = 60 * 1000
  /** Pause between in-line retries of a transiently failed refresh. */
  private bearerRefreshRetryDelayMs = 2 * 1000
  /** How soon to try again after an outage survived on a still-valid token. */
  private bearerRefreshOutageRetryMs = 30 * 1000
  /** The in-flight silent-refresh timer for the current bearer session. */
  private bearerRefreshSub: Subscription | null = null
  /** The single in-flight refresh shared by timer, guard and 401 interceptor. */
  private bearerRefreshInFlight: Observable<boolean> | null = null
  /** Cross-tab listener: adopt a token another tab just refreshed. */
  private storageListener = (e: StorageEvent) => this.onStorageEvent(e)
  constructor(
    private authSrv: AuthService,
    private store: LocalStorageService,
    private context: ContextService,
    private helper: AuthHelperService,
    private accessControlSrv: AccessControlService,
    private notificationService: NotificationService,
    private sso: SsoAuthService,
    private logoutCoordinator: LogoutCoordinatorService,
    private userFeatures: UserFeaturesService,
  ) {
    this.tokenExpirationNotificationListener$ =
      this.resetTokenExpirationTimer$.pipe(
        map((r) => r.notification),
        filter((r) => !!r),
        switchMap((time: number) => timer(time)),
        share(),
      )
    this.tokenExpirationListener$ = this.resetTokenExpirationTimer$.pipe(
      map((r) => r.redirect),
      filter((r) => !!r),
      switchMap((time: number) => timer(time)),
      share(),
    )
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.storageListener)
    }
  }

  /** In-memory guard so the bearer session is bootstrapped once per page load. */
  private bearerSessionReady = false

  isAuthenticated(verify: boolean = false): Observable<boolean> {
    let authToken = of(true)
    if (!this.helper.token) {
      // no token
      const storedToken = this.store.getItem(TOKEN_KEY)
      if (storedToken) {
        // if we have a token in the local store
        this.helper.token = storedToken
        this.helper.tokenType =
          (this.store.getItem(TOKEN_TYPE_KEY) as TokenType) || 'curo'
        verify = true // explicitly verify it is still valid
      } else {
        authToken = of(false)
        return of(false) // otherwise not authenticated
      }
    }
    // Keycloak/OIDC bearer tokens are validated locally via their `exp` claim
    // (the backend remains the real authority on every request). They do NOT
    // go through the CURO `getToken` verification below.
    if (this.helper.tokenType === 'bearer') {
      if (isJwtExpired(this.helper.token)) {
        // Access token expired (e.g. the tab sat closed a while). The refresh
        // token outlives it — try a silent Keycloak refresh before forcing a
        // re-login, then bootstrap on the renewed token.
        return this.refreshBearerToken().pipe(
          switchMap((ok) =>
            ok ? this.bootstrapBearerSession(this.helper.token) : of(false),
          ),
        )
      }
      if (this.bearerSessionReady) {
        return of(true)
      }
      return this.bootstrapBearerSession(this.helper.token)
    }
    if (verify) {
      return this.authSrv.getToken(this.helper.token).pipe(
        switchMap((token: AuthToken) => {
          if (token.expiresIn <= 0) {
            return observableThrowError(
              () => new Error('authentication token expired'),
            )
          }
          this.context.setAuthenticationContext(
            token.userId,
            token.username,
            token.domain,
          )
          this.setTokenExpirationNotification(token)
          this.userFeatures.loadForCurrentUser().subscribe()
          return this.accessControlSrv
            .loadPermissionsAsync()
            .pipe(map(() => true))
        }),
        catchError(() => {
          this.helper.token = null
          this.store.removeItem(TOKEN_KEY)
          this.accessControlSrv.flushPermissions()
          return of(false)
        }),
      )
    }
    return authToken
  }

  setTokenExpirationNotification(token: AuthToken) {
    this.authSrv
      .getExperationTimestamp(token)
      .pipe(
        first(),
        // returns about 86400 seconds = 24 hours on login
        tap((time) =>
          this.resetTokenExpirationTimer$.next({
            notification: time * 1000 - this.refreshTokenNotificationBuffer,
            redirect: time * 1000,
          }),
        ),
      )
      .subscribe()
  }

  refreshToken(): Observable<boolean> {
    return this.authSrv
      .refreshToken(this.helper.token, this.store.getItem(REFRESH_TOKEN_KEY))
      .pipe(
        first(),
        switchMap((token) => this.handleLoginResponse(token, true)),
      )
  }

  logIn(
    username: string,
    password: string,
    domain: string,
    remember: boolean = false,
  ): Observable<boolean> {
    return this.authSrv.postAuthentication(username, password, domain).pipe(
      flatMap((token: AuthToken): Observable<boolean> => {
        return this.handleLoginResponse(token, remember)
      }),
      tap((authenticated) => {
        if (authenticated) {
          this.logoutCoordinator.resetForAuthenticatedSession()
        }
      }),
    )
  }

  handleLoginResponse(
    token: AuthToken,
    remember: boolean,
  ): Observable<boolean> {
    if (token) {
      if (remember) {
        // store username and domain in local storage
        const rememberedCredentials = {
          username: token.username,
          domain: token.domain,
        }
        // remember the credentials
        this.store.setObject(MEMORY_KEY, rememberedCredentials)
        // remember the token
        this.store.setItem(TOKEN_KEY, token.tokenId)
        // remember the refresh token
        this.store.setItem(REFRESH_TOKEN_KEY, token.refreshToken)
      } else {
        this.store.removeItem(MEMORY_KEY)
        this.store.removeItem(TOKEN_KEY)
        this.store.removeItem(REFRESH_TOKEN_KEY)
        this.store.removeItem(TOKEN_TYPE_KEY)
      }
      if (token.username === 'curo') {
        this.notificationService.notify({
          msg: 'Please refrain from using the CURO user and create a personalised account.',
          type: NotificationType.Message,
        })
      }
      // set the context variables
      this.context.setAuthenticationContext(
        token.userId,
        token.username,
        token.domain,
      )

      this.helper.token = token.tokenId
      this.helper.tokenType = 'curo'
      this.setTokenExpirationNotification(token)
      this.userFeatures.loadForCurrentUser().subscribe()
      return this.accessControlSrv.loadPermissionsAsync().pipe(map(() => true))
    }
    return of(false)
  }

  /**
   * Persist a freshly captured bearer token (+ its refresh/id tokens) under
   * the `bearer` scheme WITHOUT doing the network bootstrap yet. Used by the
   * startup initializer; the AuthGuard's {@link isAuthenticated} then completes
   * the bootstrap once the runtime config (apiUrl) is loaded. Returns `false`
   * for a missing/expired access token.
   */
  registerSsoToken(
    token: string,
    refreshToken?: string,
    idToken?: string,
  ): boolean {
    if (!token || isJwtExpired(token)) {
      return false
    }
    this.helper.token = token
    this.helper.tokenType = 'bearer'
    this.bearerSessionReady = false
    this.store.setItem(TOKEN_KEY, token)
    this.store.setItem(TOKEN_TYPE_KEY, 'bearer')
    if (refreshToken) {
      this.store.setItem(REFRESH_TOKEN_KEY, refreshToken)
    }
    if (idToken) {
      this.store.setItem(ID_TOKEN_KEY, idToken)
    }
    return true
  }

  /**
   * Establish the SSO session from the captured Keycloak JWT.
   *
   * The JWT stays the credential for the whole session — every API call
   * carries `Authorization: Bearer <jwt>` (see `ApiAuthInterceptor`); the
   * backend resolves the user from the token on each request. What the legacy
   * login response used to hand the SPA is fetched once from the
   * token-identified `GET Whoami`: userId/username/domain for the UI context.
   * With those ids known, the regular permission load (`getUserRoles`) and
   * every other endpoint work unchanged over the bearer header.
   */
  private bootstrapBearerSession(token: string): Observable<boolean> {
    return this.authSrv.getSsoUser().pipe(
      switchMap((who: SsoUser) => {
        if (who?.id == null || !who.username || !who.domain) {
          const fields = Object.keys(who ?? {})
            .sort()
            .join(', ')
          rememberSsoLoginError(
            'The backend accepted the Keycloak token, but Whoami did not return the expected id, username and domain.' +
              (fields ? ` Returned fields: ${fields}` : ''),
          )
          this.clearBearerSession()
          return of(false)
        }
        this.context.setAuthenticationContext(who.id, who.username, who.domain)
        this.scheduleBearerRefresh(token)
        this.userFeatures.loadForCurrentUser().subscribe()
        return this.accessControlSrv.loadPermissionsAsync().pipe(
          map(() => {
            this.bearerSessionReady = true
            return true
          }),
        )
      }),
      catchError((err) => {
        const status = err?.status ? ` (HTTP ${err.status})` : ''
        rememberSsoLoginError(
          `Keycloak sign-in succeeded, but the CURO backend rejected the bearer session${status}. ` +
            'Whoami and the permission load must accept the Keycloak token.',
        )
        this.clearBearerSession()
        return of(false)
      }),
    )
  }

  /**
   * Schedule a silent Keycloak refresh well before the bearer token's `exp`.
   * Keycloak access tokens are short-lived (minutes), so rather than
   * warn-and-redirect like the CURO flow, we renew in the background and the
   * session simply continues. Only a failed refresh (the refresh token itself
   * expired, so the Keycloak SSO session ended) drops the user to login.
   */
  private scheduleBearerRefresh(token: string) {
    this.bearerRefreshSub?.unsubscribe()
    const due = Math.max(
      0,
      millisUntilExpiry(token) - this.refreshLeadMs(token),
    )
    this.bearerRefreshSub = timer(due)
      .pipe(switchMap(() => this.refreshBearerToken()))
      .subscribe()
  }

  /** `max(minLead, 40% of the token lifetime)`, lifetime from `iat`/`exp`. */
  private refreshLeadMs(token: string): number {
    const claims = decodeJwt(token)
    const lifetime =
      claims?.exp && claims?.iat
        ? (claims.exp - claims.iat) * 1000
        : millisUntilExpiry(token)
    return Math.max(this.bearerRefreshMinLeadMs, Math.floor(lifetime * 0.4))
  }

  /**
   * Renew the bearer session now. Safe to call from several places at once
   * (timer, guard, 401 interceptor): while a refresh is in flight every caller
   * shares it, so Keycloak's rotated refresh token is never replayed. Emits
   * `true` when a valid access token is in place afterwards; `false` after the
   * session was cleared and the user sent to login.
   */
  refreshBearerToken(): Observable<boolean> {
    if (this.helper.tokenType !== 'bearer') {
      return of(false)
    }
    if (!this.bearerRefreshInFlight) {
      this.bearerRefreshInFlight = this.refreshBearerSession().pipe(
        finalize(() => {
          this.bearerRefreshInFlight = null
        }),
        shareReplay(1),
      )
    }
    return this.bearerRefreshInFlight
  }

  /**
   * Exchange the stored Keycloak refresh token for a fresh access token, update
   * the persisted session, and re-arm the refresh timer. Emits `true` when a
   * valid access token is in place afterwards. Only a *definitive* Keycloak
   * refusal clears the session and sends the user to login (`false`); a
   * transient failure (network blip, Keycloak restart) is retried, and — as
   * long as the current access token is still valid — survived.
   */
  private refreshBearerSession(): Observable<boolean> {
    const refreshToken = this.store.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      return this.failBearerRefresh()
    }
    // `defer` (not `from`) so the `retry` below re-runs the exchange instead
    // of replaying the settled promise.
    return defer(() => this.sso.refreshAccessToken(refreshToken)).pipe(
      retry({
        count: 2,
        delay: (err) =>
          err instanceof SsoTransientRefreshError
            ? timer(this.bearerRefreshRetryDelayMs)
            : observableThrowError(() => err),
      }),
      switchMap((tokens) => {
        if (!tokens?.accessToken || isJwtExpired(tokens.accessToken)) {
          // Another tab may have rotated the refresh token in the meantime
          // (Keycloak refuses a consumed one). Adopt what that tab stored
          // instead of tearing the shared session down.
          return this.adoptStoredBearerSession(refreshToken)
            ? of(true)
            : this.failBearerRefresh()
        }
        this.adoptBearerTokens(tokens)
        return of(true)
      }),
      catchError((err) =>
        err instanceof SsoTransientRefreshError
          ? this.surviveTransientRefreshFailure(refreshToken)
          : this.adoptStoredBearerSession(refreshToken)
            ? of(true)
            : this.failBearerRefresh(),
      ),
    )
  }

  /**
   * Keycloak stayed unreachable through the in-line retries. A brief outage or
   * a laptop waking from sleep must not force a re-login: while the current
   * access token still has life left, keep the session and re-try shortly.
   * Only when the access token is already expired too (nothing left to make
   * requests with) is the session ended.
   */
  private surviveTransientRefreshFailure(
    usedRefreshToken: string,
  ): Observable<boolean> {
    if (this.adoptStoredBearerSession(usedRefreshToken)) {
      // Another tab got through during the outage.
      return of(true)
    }
    const token = this.helper.token
    if (token && this.helper.tokenType === 'bearer' && !isJwtExpired(token)) {
      this.bearerRefreshSub?.unsubscribe()
      this.bearerRefreshSub = timer(this.bearerRefreshOutageRetryMs)
        .pipe(switchMap(() => this.refreshBearerToken()))
        .subscribe()
      return of(true)
    }
    return this.failBearerRefresh()
  }

  /** Persist freshly minted tokens and re-arm the timer. */
  private adoptBearerTokens(tokens: {
    accessToken: string
    refreshToken?: string
    idToken?: string
  }) {
    this.helper.token = tokens.accessToken
    this.helper.tokenType = 'bearer'
    this.store.setItem(TOKEN_KEY, tokens.accessToken)
    this.store.setItem(TOKEN_TYPE_KEY, 'bearer')
    // Keycloak rotates the refresh token: persist the new one or the next
    // refresh would replay a consumed token and fail.
    if (tokens.refreshToken) {
      this.store.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken)
    }
    if (tokens.idToken) {
      this.store.setItem(ID_TOKEN_KEY, tokens.idToken)
    }
    this.scheduleBearerRefresh(tokens.accessToken)
  }

  /**
   * If another tab already refreshed (the stored refresh token differs from
   * the one we tried and the stored access token is still valid), take over
   * that session. Returns `true` when it did.
   */
  private adoptStoredBearerSession(usedRefreshToken: string): boolean {
    const storedRefresh = this.store.getItem(REFRESH_TOKEN_KEY)
    const storedAccess = this.store.getItem(TOKEN_KEY)
    if (
      !storedRefresh ||
      storedRefresh === usedRefreshToken ||
      !storedAccess ||
      this.store.getItem(TOKEN_TYPE_KEY) !== 'bearer' ||
      isJwtExpired(storedAccess)
    ) {
      return false
    }
    this.helper.token = storedAccess
    this.helper.tokenType = 'bearer'
    this.scheduleBearerRefresh(storedAccess)
    return true
  }

  /** Another tab changed the stored access token: follow it. */
  private onStorageEvent(e: StorageEvent) {
    if (e.key !== 'curo-' + TOKEN_KEY || this.helper.tokenType !== 'bearer') {
      return
    }
    if (!e.newValue) {
      // The other tab logged out; drop our local session too.
      if (this.helper.token) {
        this.clearBearerSession()
        this.helper.redirect()
      }
      return
    }
    if (e.newValue !== this.helper.token && !isJwtExpired(e.newValue)) {
      this.helper.token = e.newValue
      this.scheduleBearerRefresh(e.newValue)
    }
  }

  /** The Keycloak session is gone: clear local state and go to login. */
  private failBearerRefresh(): Observable<boolean> {
    const ownsLogout = this.logoutCoordinator.begin('refresh-failure')
    this.clearBearerSession()
    if (ownsLogout) {
      this.logoutCoordinator.suppressNextAutomaticLogin('refresh-failure')
      this.logoutCoordinator.record('local-session-cleared', 'refresh-failure')
      this.logoutCoordinator.record(
        'local-login-navigation-started',
        'refresh-failure',
      )
      this.helper.redirect()
    }
    return of(false)
  }

  /** Forget the bearer token + derived state (local only). */
  private clearBearerSession() {
    this.bearerRefreshSub?.unsubscribe()
    this.bearerRefreshSub = null
    this.bearerSessionReady = false
    this.helper.token = null
    this.helper.tokenType = 'curo'
    this.store.removeItem(TOKEN_KEY)
    this.store.removeItem(REFRESH_TOKEN_KEY)
    this.store.removeItem(TOKEN_TYPE_KEY)
    this.store.removeItem(ID_TOKEN_KEY)
    this.accessControlSrv.flushPermissions()
    this.userFeatures.resetForLogout()
    this.context.clear()
  }

  logOut(): Observable<void> {
    if (!this.logoutCoordinator.begin('user')) {
      return EMPTY
    }

    // Bearer (Keycloak) sessions have no server-side CURO token to delete.
    // Clear local state, then hand off to Keycloak's end-session endpoint so
    // its SSO cookie is cleared too — otherwise the next "Sign in with
    // Keycloak" returns silently as the same user. Keycloak redirects back to
    // the login page afterwards.
    if (this.helper.tokenType === 'bearer') {
      const idToken = this.store.getItem(ID_TOKEN_KEY)
      this.clearBearerSession()
      this.logoutCoordinator.record('local-session-cleared', 'user')
      const logoutUrl = this.sso.buildLogoutUrl(idToken)
      if (logoutUrl) {
        this.logoutCoordinator.suppressNextAutomaticLogin('user')
        this.logoutCoordinator.record(
          'keycloak-navigation-started',
          'user',
          logoutUrl,
        )
        window.location.replace(logoutUrl)
        return EMPTY
      }
      this.logoutCoordinator.suppressNextAutomaticLogin('user')
      this.logoutCoordinator.record('local-login-navigation-started', 'user')
      return of(undefined)
    }
    const currentToken = this.helper.token
    return this.authSrv.deleteToken(currentToken).pipe(
      catchError(() => of(null)),
      defaultIfEmpty(null),
      map((): void => {
        this.helper.token = null
        this.store.removeItem(TOKEN_KEY)
        this.store.removeItem(REFRESH_TOKEN_KEY)
        this.store.removeItem(TOKEN_TYPE_KEY)
        this.accessControlSrv.flushPermissions()
        this.userFeatures.resetForLogout()
        this.context.clear()
        this.logoutCoordinator.record('local-session-cleared', 'user')
        this.logoutCoordinator.suppressNextAutomaticLogin('user')
        this.logoutCoordinator.record('local-login-navigation-started', 'user')
      }),
    )
  }

  get rememberedCredentials(): object {
    const mem = this.store.getObject(MEMORY_KEY)
    if (mem) {
      return mem
    }
    return {}
  }
}
